import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslocoPipe, TranslocoService} from '@ngneat/transloco';
import {MenuItem} from 'primeng/api';
import {Observable, Subscription} from 'rxjs';
import {AuthGuard} from 'src/app/common/auth-guard.service';
import {ComponentCanDeactivate} from 'src/app/common/can-deactivate/component-can-deactivate';
import {Machine} from 'src/app/model/machine';
import {MachineDetailsService} from 'src/app/services/machine-details.service';

@Component({
  selector: 'app-machine-details',
  templateUrl: './machine-details.component.html',
  styleUrls: ['./machine-details.component.scss'],
  providers: [TranslocoPipe]
})
export class MachineDetailsComponent implements OnInit, ComponentCanDeactivate, OnDestroy {

  public machine: Machine;

  public isMachineLoaded: boolean = false;

  public menuItems: MenuItem[];
  public activeMenuItem: MenuItem;

  private machineDetailsSubscription: Subscription;


  constructor(
    private readonly router: Router,
    private readonly i18n: TranslocoService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly machineDetailsService: MachineDetailsService,
    public authGuard: AuthGuard
  ) {
  }

  ngOnInit(): void {
    this.isMachineLoaded = false;

    this.activatedRoute.params.subscribe(params => {
      this.machineDetailsService.init(params.machineUuid);
    });

    this.machineDetailsService.routeChanged.subscribe(route => {
      this.router.navigate(['/machines/' + this.machine.machineUuid + '/details/' + route]).then(() => this.buildTabMenu());
    });

    this.machineDetailsSubscription = this.machineDetailsService.$notifier.subscribe(machineDetails => {
      if (machineDetails.machineLoading === false) {
        this.machine = machineDetails.machine;
        this.isMachineLoaded = true;
      }
    });
    this.buildTabMenu();
  }

  private buildTabMenu(): void {
    this.i18n.selectTranslation().subscribe(() => {
      this.menuItems = [
        {label: this.i18n.translate('common.general'), icon: 'pi pi-fw pi-map', routerLink: ['general']},
        {label: this.i18n.translate('model.data'), icon: 'pi pi-fw pi-info', routerLink: ['data']},
        {label: this.i18n.translate('model.charts'), icon: 'pi pi-fw pi-chart-bar', routerLink: ['charts']}
      ];

      let i = 3;
      if (this.authGuard.hasPermission('PERMISSION_VIEW_ALARMS')) {
        this.menuItems[i] = {
          label: this.i18n.translate('model.maintenance'),
          icon: 'pi pi-fw pi-exclamation-triangle',
          routerLink: ['issues']
        };
        i++;
      }
      if (this.authGuard.hasPermission('PERMISSION_MANAGE_AUDITS')) {
        this.menuItems[i] = {
          label: this.i18n.translate('model.audits'),
          icon: 'pi pi-fw pi-book',
          routerLink: ['audits']
        };
        i++;
      }
      if (this.authGuard.hasPermission('PERMISSION_MANAGE_ADVANCED_MACHINES')) {
        this.menuItems[i] = {
          label: this.i18n.translate('model.configuration'),
          icon: 'pi pi-fw pi-cog',
          routerLink: ['config']
        };
      }

      this.defineActiveMenuItem();
    });
  }

  private defineActiveMenuItem(): void {
    const activeRoute = this.router.url.split('/').pop();
    this.activeMenuItem = this.menuItems.find(item => item.routerLink[0] === activeRoute);
  }

  navigateToMachineEdition(): void {
    this.router.navigate(['../edit'], {relativeTo: this.activatedRoute})
  }

  canDeactivate(): boolean | Promise<boolean> | Observable<boolean> {
    return true;
  }

  ngOnDestroy(): void {
    if (this.machineDetailsSubscription != null) this.machineDetailsSubscription.unsubscribe();
    this.machineDetailsService.cleanNotifier();
  }
}
